import React from 'react';
import { SteuerAufschluesselungZeile, XmlValidierung } from '../types';
import {
  formatLabel,
  gutschriftLabel,
  istGutschrift,
  hatSteuerBetraege,
  kategorieText,
  satzText,
  betragText,
  validierungAnzeige,
} from '../lib/eRechnung';
import './ERechnungDetail.css';

/** Badges "E-Rechnung · …" und "Gutschrift"; rendert nichts, wenn beides nicht zutrifft. */
export const ERechnungBadges: React.FC<{
  format?: string | null;
  typCode?: string | null;
}> = ({ format, typCode }) => {
  const label = formatLabel(format);
  if (!label && !istGutschrift(typCode)) return null;
  return (
    <span className="erechnung-badges">
      {label && <span className="erechnung-badge">{label}</span>}
      {istGutschrift(typCode) && (
        <span className="erechnung-badge erechnung-badge--gutschrift">{gutschriftLabel(typCode)}</span>
      )}
    </span>
  );
};

/** Ergebnis der XML-Prüfung (OK neutral/grün, FEHLER Warnung, UNGEPRUEFT grau). */
export const ERechnungValidierung: React.FC<{ validierung?: XmlValidierung | null }> = ({ validierung }) => {
  const anzeige = validierungAnzeige(validierung);
  if (!anzeige) return null;
  const alertKlasse =
    anzeige.klasse === 'fehler' ? 'k-alert--warn' : anzeige.klasse === 'ok' ? 'k-alert--ok' : '';
  return (
    <div className={`k-alert ${alertKlasse} erechnung-validierung erechnung-validierung--${anzeige.klasse}`}>
      <div className="k-alert__title">{anzeige.titel}</div>
      <div className="k-alert__msg">{anzeige.hinweis}</div>
      {anzeige.meldungen.length > 0 && (
        <ul className="erechnung-meldungen">
          {anzeige.meldungen.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
    </div>
  );
};

interface ERechnungDetailProps {
  format?: string | null;
  typCode?: string | null;
  steuer?: SteuerAufschluesselungZeile[] | null;
  validierung?: XmlValidierung | null;
  /** Gesetzt, wenn eine Original-XML heruntergeladen werden kann. */
  onXmlHerunterladen?: () => void;
}

/** Detailbereich einer Eingangs-E-Rechnung: XML-Download, Steuer-Aufschlüsselung, Prüfstatus. */
const ERechnungDetail: React.FC<ERechnungDetailProps> = ({
  format, typCode, steuer, validierung, onXmlHerunterladen,
}) => {
  const zeilen = steuer ?? [];
  const mitBetraegen = hatSteuerBetraege(zeilen);
  return (
    <div className="erechnung-detail">
      <div className="erechnung-detail__kopf">
        <ERechnungBadges format={format} typCode={typCode} />
        {onXmlHerunterladen && (
          <button type="button" className="action-btn pdf" onClick={onXmlHerunterladen}
                  title="Original-XML der E-Rechnung herunterladen">
            XML herunterladen
          </button>
        )}
      </div>

      {zeilen.length > 0 && (
        <table className="erechnung-steuer">
          <caption>Steuer-Aufschlüsselung</caption>
          <thead>
            <tr>
              <th>Satz</th>
              <th>Kategorie</th>
              {mitBetraegen && <th className="num">Basis</th>}
              {mitBetraegen && <th className="num">Steuer</th>}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z, i) => (
              <tr key={i}>
                <td>{satzText(z.satz)}</td>
                <td>
                  {kategorieText(z.kategorie)}
                  {z.befreiungsgrund && <div className="erechnung-steuer__grund">{z.befreiungsgrund}</div>}
                </td>
                {mitBetraegen && <td className="num">{betragText(z.basis)}</td>}
                {mitBetraegen && <td className="num">{betragText(z.betrag)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ERechnungValidierung validierung={validierung} />
    </div>
  );
};

export default ERechnungDetail;
